<template>
	<div ref="graphRef" :style="graphStyle">
		<!-- draggableNodes must stay true: graphly 1.5.3's dragEnd routes
		     non-draggable drags into dragNewLinkEnd(), which crashes on an
		     undefined newLink. Layout re-pins node positions on every data
		     change, so dragging is cosmetic. -->
		<GraphlyD3
			ref="graphly"
			:dark="false"
			:env-gravity="mode === 'map' ? -8000 : 0"
			:draggableNodes="true"
			@node-click="onNodeClick"
			@node-double-click="onNodeDoubleClick"
			@node-drag-end="handleDragEnd"
		/>
	</div>
</template>

<script setup lang="ts">
	import { ref, computed, onMounted } from 'vue';
	import { type Link } from '@livereader/graphly-d3';
	import GraphlyD3 from '@livereader/graphly-d3/component/vue3';
	import { Event, ForceSimulation, renderTemplate, type Graph, type Node } from '@livereader/graphly-d3';
	import '@livereader/graphly-d3/style.css';
	import { type GraphNode } from '../models/graphNode';

	const graphRef: Ref<HTMLElement | null> = ref(null);
	const graphHeight: Ref<number> = ref(0);
	const graphWidth: Ref<number> = ref(0);
	const linkColor = '#00ffea';

	type Orientation = 'Top' | 'Bottom' | 'Left' | 'Right';

	const props = defineProps({
		width: {
			type: [String, Number],
			default: '600px',
		},
		height: {
			type: [String, Number],
			default: '400px',
		},
		data: {
			type: [String],
			default: '',
		},
		graph: {
			type: Object as PropType<Graph>,
			default: () => ({
				nodes: [],
				links: [],
			}),
		},
		orientation: {
			type: String, // Using String as the prop type
			default: 'Top',
			validator: (value: string): value is Orientation => {
				return ['Top', 'Bottom', 'Left', 'Right'].includes(value);
			},
		},
		// 'focus' = arc layout around the centre node; 'map' = force-directed
		// full graph with draggable, persisted positions.
		mode: {
			type: String as PropType<'focus' | 'map'>,
			default: 'focus',
		},
		onNodeClick: Function as PropType<(e: Event, d: Node) => Promise<void>>,
		onNodeDoubleClick: Function as PropType<(e: Event, d: Node) => Promise<void>>,
		onNodeDragEnd: Function as PropType<(e: Event, d: Node, position: { x: number; y: number }) => void>,
	});

	const graphStyle = computed(() => ({
		width: props.width,
		height: props.height,
	}));

	const graphly = ref<any>(null);
	const simulation = computed<ForceSimulation>(() => graphly.value.simulation);

	const findParentNodes = (graph: Graph, centralNodeId: string): Node[] => {
		return graph.links
			.filter((link) => link.target === centralNodeId)
			.map((link) => graph.nodes.find((node) => node.id === link.source))
			.filter((node) => node !== undefined) as Node[];
	};

	const findChildNodes = (graph: Graph, centralNodeId: string): Node[] => {
		return graph.links
			.filter((link) => link.source === centralNodeId)
			.map((link) => graph.nodes.find((node) => node.id === link.target))
			.filter((node) => node !== undefined) as Node[];
	};

	const updateGraphCoordinates = (graph: Graph): void => {
		const centralNode: Node = graph.nodes[0];
		const radius = 200; // Radius of the arcs

		// Function to position a node in an arc
		const positionInArc = (
			node: Node,
			baseAngle: number,
			index: number,
			totalNodes: number,
			isParent: boolean
		): void => {
			// Ensure centralNode.x and centralNode.y are set before computing positions
			if (centralNode.x === undefined || centralNode.y === undefined) {
				console.error('Central node coordinates are undefined');
				return;
			}
			// Calculate the spread angle range (less than 180 degrees to avoid placing nodes at the vertical line)
			const spreadAngle = Math.PI / (totalNodes + 1);
			// Calculate the actual angle for the node. SVG y points down, so the
			// two arcs need opposite sweep directions for array index 0 to land
			// at the TOP of both arcs (draw order reads top to bottom).
			const angle = isParent
				? baseAngle - spreadAngle * (index + 1)
				: spreadAngle * (index + 1) - baseAngle;
			node.x = centralNode.x + radius * Math.cos(angle);
			node.y = centralNode.y + radius * Math.sin(angle);
			console.log(`${angle} :` + JSON.stringify(node));
		};

		// Positioning the central node
		centralNode.x = 0;
		centralNode.y = 0;

		// Positioning parent nodes
		const parentNodes = findParentNodes(graph, centralNode.id);
		parentNodes.forEach((parent, index) => {
			// Start at 270 degrees and move clockwise
			positionInArc(parent, Math.PI * 1.5, index, parentNodes.length, true);
			parent.payload.color = '#ff0000';
		});

		// Positioning child nodes
		const childNodes = findChildNodes(graph, centralNode.id);
		childNodes.forEach((child, index) => {
			// Start at 90 degrees and move clockwise
			positionInArc(child, Math.PI * 0.5, index, childNodes.length, false);
			child.payload.color = '#00ff00';
		});
	};

	const reactiveGraph = computed(() => {
		if (props.data.length > 0) {
			try {
				return JSON.parse(props.data);
			} catch (e) {
				console.error('Error parsing JSON:', e);
				return props.graph; // Fallback to default graph in case of error
			}
		} else {
			return props.graph;
		}
	});

	
	// Graphly's render() is async and not re-entrant: overlapping calls can
	// wire one graph's links against another graph's nodes, leaving the d3
	// link force with unresolved string ids ("Cannot create property 'vx' on
	// string ..."). Store actions fire several graph changes in quick
	// succession (delete = unlink + upsert + re-centre), so renders are
	// serialised, and bursts collapse into one render of the latest graph.
	let renderQueue: Promise<void> = Promise.resolve();
	let renderScheduled = false;

	// `forced` clears graphly's nodeDataStore + DOM before rebuilding (used to
	// recover from a poisoned store after a failed render).
	const runRender = async (forced: boolean) => {
		const graph = reactiveGraph.value;
		// Focus mode lays nodes on arcs; Map mode keeps the saved/force
		// positions already on the graph nodes (no arc, no recolour).
		if (props.mode === 'focus') {
			updateGraphCoordinates(graph);
		} else {
			// Map re-renders (selection recolour, edits, drag-persist) rebuild
			// fresh node objects; carry the previous live positions over so
			// unsaved nodes keep their settled spot instead of re-spawning.
			const prev = (simulation.value as { graph?: { nodes?: Array<{ id: string; x?: number; y?: number }> } }).graph?.nodes ?? [];
			const posById = new Map(prev.map((n) => [n.id, { x: n.x, y: n.y }]));
			for (const node of graph.nodes as Array<{ id: string; x?: number; y?: number }>) {
				if (node.x == null && node.y == null) {
					const p = posById.get(node.id);
					if (p && p.x != null && p.y != null) {
						node.x = p.x;
						node.y = p.y;
					}
				}
			}
		}
		await simulation.value.render(graph, 0.05, forced);
	};

	const scheduleRender = () => {
		if (renderScheduled) {
			return;
		}
		renderScheduled = true;
		renderQueue = renderQueue
			.then(async () => {
				renderScheduled = false;
				try {
					await runRender(false);
				} catch (error) {
					// A failed render leaves graphly's nodeDataStore inconsistent,
					// so every later render throws too. A forced render clears the
					// store + DOM and rebuilds from scratch, self-healing.
					console.error('Graph render failed, retrying forced:', error);
					await runRender(true);
				}
			})
			.catch((error) => {
				console.error('Graph render failed (forced retry also failed):', error);
			});
	};

	watch(reactiveGraph, scheduleRender);

	// graphly nulls fx/fy on drag end (releasing the pin), so a dropped node
	// would drift back under the force. Re-pin the live node to where it was
	// dropped FIRST (no flicker), then let the parent persist it.
	const handleDragEnd = (e: Event, node: Node, position: { x: number; y: number }) => {
		if (props.mode !== 'map') {
			return;
		}
		node.x = position.x;
		node.y = position.y;
		(node as { fx?: number; fy?: number }).fx = position.x;
		(node as { fx?: number; fy?: number }).fy = position.y;
		props.onNodeDragEnd?.(e, node, position);
	};

	onMounted(async () => {
		if (graphRef.value) {
			console.log('Height of the div:', graphRef.value.clientHeight, 'pixels');
			graphHeight.value = graphRef.value.clientHeight;
			graphWidth.value = graphRef.value.clientWidth;
		}
		simulation.value.templateStore.add('circle', usePolygonShape(30));
		simulation.value.templateStore.add('triangle', usePolygonShape(3));
		simulation.value.templateStore.add('square', usePolygonShape(4, 45));
		simulation.value.templateStore.add('diamond', usePolygonShape(4));
		simulation.value.templateStore.add('pentagon', usePolygonShape(5));
		simulation.value.templateStore.add('hexagon', usePolygonShape(6, 30));
		simulation.value.templateStore.add('octagon', usePolygonShape(8, 22.5));
		scheduleRender();
	});
</script>

<style lang="scss">
	svg .gly-node {
		fill: none !important;
		stroke-width: 8px;
		stroke: aqua;
	}
</style>
